import { SessionStore } from "../../uidispatcherapi/SessionStore";

import { getAuthUtil } from "auth_util";
import { ENTITLEMENTS } from "auth_util";
import NaviDevsUtil from "./NaviDefsUtil";
import CountryUtil from "../../uidispatcherapi/CountryUtil";

const AuthUtil = getAuthUtil(SessionStore);

/**
 * this module provides a list of all navigations items
 * the keys are
 *   text: intl-key of text
 *   title: intl-key of title tag (for tooltip), will be filled with text if not there
 *   url: path for react router
 *   trackAs: name for tracking (was "name" in headeremployee)
 *   testId: id for Selenium-test, will be added to class in link
 *   iconId: optional if this should be shown as icon (e.g. home)
 *   beta: optional, true if should be visible only for beta users
 *   neededRights: optional, if set only users with this rights are allowed, (currently entitlement, in future maybe role))
 *   isOrderFulfillment: TODO (hm)  document this
 * The parent-node include these fields:
 *   extraInfo: set when a subpanel should display additional information, e.g. customer
 *   children: pages that are in MenuItem
 *   otherUrls: pages not in Menu, but should make the item marked as current.
 * Soon to include:
 *     optional query parts: e.g. the customer parameter
 *
 *
 */

type MenuItem = {
    text: string;
    title?: string;
    trackAs?: string;
    testId?: string;
    url?: string;
    iconId?: string;
    beta?: boolean;
    neededRights?: string;
    isOrderFulfillment?: boolean;
    isNonBackendUrl?: boolean; // For the links that do not start with /backend/... and are not orderfulfillment,
    hasOwnCustomerSelect?: boolean;
    otherUrls?: Array<string>;
    isLogout?: boolean;
    children?: typeof undefined; // MenuItems don't have children,
    top?: typeof undefined; // MenuItems don't have top
};

type MenuParent = {
    top: MenuItem;
    children: Array<MenuItem>;
    extraInfo?: string;
    beta?: boolean;
    isOrderFulfillment?: boolean;
    trackAs?: string;
    url?: typeof undefined;
};

type MenuNode = MenuItem | MenuParent;

const ENTITLEMENT_EX_CHANGE_STORE_ACTIVATION = "xSTACT";
const ENTITLEMENT_DEV_ROLE = "cDI";

const _items = {
    employeeStartItem: {
        text: "PAGE_TITLES.EMPLOYEE_START",
        title: "NAVI_TITLETAGS.EMPLOYEE_START",
        trackAs: "employee-start",
        testId: "employee-start",
        url: "/employee-start",
        iconId: "icon-home",
        isOrderFulfillment: true
    },
    customerTopItem: {
        text: "NAVI_TITLES.CUSTOMER",
        title: "NAVI_TITLETAGS.CUSTOMER",
        testId: "navi-top-customer",
        trackAs: "top/customer" // TODO: how to track these?
    },
    ordercaptureItem: {
        text: "PAGE_TITLES.ORDER_CAPTURE",
        trackAs: "ordercapture",
        testId: "navi-ordercapture",
        url: "/",
        otherUrls: [
            "/cart",
            "/mario-cart",
            "/catalogue",
            "/category",
            "/list",
            "/shoppingList",
            "/merchantInformation",
            "/merchantArticles",
            "/historylist",
            "/savedCart",
            "/portal",
            "/promotions",
            "/addnewaddress",
            "/pv",
            "/reorder",
            "/selectAddress/",
            "/storelist",
            "/metrolist",
            "/backoffice/customer/search/",
            "/search",
            "/public",
            "/checkout",
            "/faq",
            "/listOverview"
        ]
    },
    fastOrderEntryItem: {
        text: "PAGE_TITLES.ORDER_ENTRY",
        trackAs: "fastorderentry",
        testId: "navi-fastorderentry",
        url: "/order-entry/search?nonEmpty=true&active=true"
    },
    orderFulfillmentTopItem: {
        text: "NAVI_TITLES.ORDER_FULFILLMENT",
        trackAs: "orderfulfillment",
        testId: "navi-top-orderfulfillment"
    },
    orderItem: {
        text: "PAGE_TITLES.ORDER_MANAGEMENT",
        url: "/order",
        testId: "navi-order",
        trackAs: "order",
        isOrderFulfillment: true
    },
    pickingItem: {
        text: "PAGE_TITLES.PICKING",
        trackAs: "picking",
        testId: "navi-picking",
        url: "/picking",
        otherUrls: ["/printPickingLists", "/printPrePickingLists"],
        isOrderFulfillment: true
    },
    combineordersItem: {
        text: "PAGE_TITLES.COMBINE_ORDERS",
        trackAs: "combineorders",
        testId: "navi-combineorders",
        url: "/combineorders",
        isOrderFulfillment: true
    },
    tillsconfsItem: {
        text: "PAGE_TITLES.TILLSCONFS",
        trackAs: "tillsconfs",
        testId: "navi-tillsconfs",
        url: "/tillsconfs",
        neededRights: ENTITLEMENTS.OF_INVOICE_STORE_TILLSCONFS,
        isOrderFulfillment: true
    },
    transportationtoursItem: {
        text: "PAGE_TITLES.TRANSPORTATION_TOURS",
        trackAs: "transportationtours",
        testId: "navi-transportationtours",
        url: "/transportationtours",
        otherUrls: ["/printTripTicket"],
        isOrderFulfillment: true
    },
    transportGoodsItem: {
        text: "PAGE_TITLES.CUSTOMER_MANAGEMENT", // TODO(KM): change this, as
        trackAs: "customer", // this is currently transport goods of the customer
        testId: "navi-customer",
        url: "/customer/transportgoods",
        otherUrls: ["/backoffice/customer/search/customer%2FtransportGoods"],
        isOrderFulfillment: true
    },
    locationItem: {
        text: "PAGE_TITLES.ADMINISTRATION",
        trackAs: "location",
        testId: "navi-location",
        url: "/location",
        isOrderFulfillment: true
    },
    storageGroupMappingsItem: {
        text: "NAVI_TITLES.STORAGE_GROUP_MAPPINGS",
        trackAs: "storageGroupMappings",
        testId: "navi-storage-group-mappings",
        url: "/storageGroupMappings",
        beta: true,
        isOrderFulfillment: true
    },
    depotTopItem: {
        text: "PAGE_TITLES.DEPOT",
        trackAs: "depot",
        testId: "navi-top-depot"
    },
    overviewsItem: {
        text: "NAVI_TITLES.OVERVIEW",
        trackAs: "overviews",
        testId: "navi-overview",
        url: "/overview",
        beta: true,
        isOrderFulfillment: true
    },
    jobsItem: {
        text: "NAVI_TITLES.JOBS",
        trackAs: "jobs",
        testId: "navi-jobs",
        url: "/jobs",
        beta: true,
        neededRights: ENTITLEMENTS.OF_JOB_READ,
        isOrderFulfillment: true
    },
    resourcesTopItem: {
        text: "NAVI_TITLES.RESOURCES",
        trackAs: "resources",
        testId: "navi-resources"
    },
    adminTopItem: {
        text: "NAVI_TITLES.ADMINISTRATION",
        trackAs: "admin",
        testId: "navi-admin"
    },
    depotconfigItem: {
        text: "NAVI_TITLES.DEPOT_CONFIG",
        trackAs: "depotconfig",
        testId: "navi-depotconfig",
        url: "/depotconfiguration",
        beta: true,
        neededRights: ENTITLEMENTS.OF_LOCATION_DATA_READ,
        isOrderFulfillment: true
    },
    depotSettingsItem: {
        text: "DEPOTSETTINGS_PAGE.PAGE_TITLE",
        trackAs: "depotsetting",
        testId: "navi-depotsetting",
        url: "/depotsettings",
        beta: true,
        neededRights: ENTITLEMENTS.OF_LOCATION_DATA_READ,
        isOrderFulfillment: true
    },
    storageGroupsItem: {
        text: "NAVI_TITLES.STORAGE_GROUPS",
        trackAs: "storageGroups",
        testId: "navi-storage-groups",
        url: "/storageGroups",
        beta: true,
        isOrderFulfillment: true
    },
    importDeliveryItem: {
        text: "NAVI_TITLES.IMPORT_DELIVERY",
        trackAs: "importDelivery",
        testId: "navi-import-delivery",
        url: "/importDelivery",
        beta: true,
        isOrderFulfillment: true
    },
    depotAreasItem: {
        text: "NAVI_TITLES.DEPOT_AREAS",
        trackAs: "depot-areas",
        testId: "navi-depot-areas",
        url: "/depot-areas",
        beta: true,
        isOrderFulfillment: true
    },
    articleLocationsItem: {
        text: "NAVI_TITLES.ARTICLE_LOCATIONS",
        trackAs: "articlelocations",
        testId: "navi-articlelocation",
        url: "/articlelocations",
        beta: true,
        neededRights: ENTITLEMENTS.OF_LOCATION_DATA_READ,
        isOrderFulfillment: true
    },
    returnsItems: {
        text: "PAGE_TITLES.RETURNS",
        trackAs: "returns",
        testId: "navi-returns",
        url: "/returns",
        otherUrls: ["/backoffice/customer/search/returns"],
        isOrderFulfillment: true
    },
    profileTopItem: {
        text: "NAVI_TITLES.PROFILE",
        trackAs: "account",
        testId: "navi-top-profile"
    },
    logoutItem: {
        text: "PAGE_TITLES.LOGOUT",
        trackAs: "logout",
        testId: "navi-logout",
        url: "/logout",
        isLogout: true
    },
    devTopItem: {
        text: "NAVI_TITLES.DEV",
        trackAs: "dev",
        testId: "navi-top-dev"
    },
    abExperimentItem: {
        text: "PAGE_TITLES.AB_EXPERIMENTS",
        trackAs: "ab_experiments",
        url: "/backoffice/ab-experiments",
        testId: "navi-ab_experiments",
        neededRights: ENTITLEMENT_DEV_ROLE
    },
    wavesItem: {
        text: "NAVI_TITLES.WAVES",
        trackAs: "waves",
        testId: "navi-waves",
        url: "/wave-management",
        beta: true,
        isOrderFulfillment: true
    },
    mopConfigItem: {
        text: "NAVI_TITLES.MOP_CONFIGURATION",
        trackAs: "mop-configuration",
        testId: "navi-mop-configuration",
        url: "/mop-configuration",
        beta: true,
        isOrderFulfillment: true
    },
    depotPrinterConfigItem: {
        text: "NAVI_TITLES.DEPOT_PRINTER_CONFIGURATION",
        trackAs: "depot-printer-configuration",
        testId: "navi-depot-printer-configuration",
        url: "/printer-configuration",
        beta: true,
        isOrderFulfillment: true
    },
    tourToGateAssignmentsItem: {
        text: "NAVI_TITLES.TOUR_GATE_ASSIGNMENTS",
        trackAs: "tour-gate-assignments",
        testId: "navi-tour-gate-assignments",
        url: "/tour-gate-assignments",
        beta: true,
        isOrderFulfillment: true
    },
    reservationItem: {
        text: "NAVI_TITLES.RESERVATIONS",
        trackAs: "stockReservations",
        testId: "navi-stock-reservations",
        url: "/reservations",
        beta: true,
        isOrderFulfillment: true
    },
    labourManagementItem: {
        text: "NAVI_TITLES.DEPOT_LABOUR_MANAGEMENT",
        trackAs: "labourManagement",
        testId: "navi-labour-management",
        url: "/employees",
        beta: true,
        isOrderFulfillment: true
    },
    backofficeTopItem: {
        text: "NAVI_TITLES.BACKOFFICE",
        trackAs: "backoffice",
        testId: "navi-backoffice"
    },
    storelistManagementItem: {
        text: "NAVI_TITLES.STORELIST_MANAGEMENT",
        trackAs: "storelist",
        testId: "navi-storelist",
        url: "/backoffice/storelistmanagement",
        neededRights: ENTITLEMENTS.EX_STORE_LIST_RW
    },
    blackListArticlesItem: {
        text: "NAVI_TITLES.ARTICLES_BLACKLIST",
        trackAs: "articleBlacklist",
        testId: "navi-articleBlacklist",
        url: "/evaluate.backofficeui/#/blacklist",
        neededRights: ENTITLEMENTS.EV_OVERRIDES,
        isNonBackendUrl: true
    },
    articleGroupsItem: {
        text: "NAVI_TITLES.ARTICLE_GROUPS",
        trackAs: "articleGroups",
        testId: "navi-articleGroups",
        url: "/evaluate.backofficeui/#/article_groups",
        neededRights: ENTITLEMENTS.EV_OVERRIDES,
        isNonBackendUrl: true
    },
    leadtimeArticlesItem: {
        text: "NAVI_TITLES.ARTICLES_LEADTIME",
        trackAs: "articleLeadtime",
        testId: "navi-articleLeadtime",
        url: "/evaluate.backofficeui/#/fallbacks",
        neededRights: ENTITLEMENTS.EV_OVERRIDES,
        isNonBackendUrl: true
    },
    personallistManagement: {
        text: "NAVI_TITLES.PERSONALLIST_MANAGEMENT",
        trackAs: "personallistManagement",
        testId: "navi-personallist-management",
        url: "/backoffice/personallists/create"
    },
    bannerManagementItem: {
        text: "NAVI_TITLES.BANNER_MANAGEMENT",
        trackAs: "bannerManagement",
        testId: "navi-banner-management",
        url: "/backoffice/bannermanagement/campaigns"
    },
    activationTopItem: {
        text: "NAVI_TITLES.ACTIVATION",
        trackAs: "activation",
        testId: "navi-activation"
    },
    storeActivationItem: {
        text: "NAVI_TITLES.STORE_ACTIVATION",
        trackAs: "storeActivation",
        testId: "navi-store-activation",
        url: "/backoffice/store/activation",
        neededRights: ENTITLEMENT_EX_CHANGE_STORE_ACTIVATION
    },
    categoryConfigurationItem: {
        text: "NAVI_TITLES.CATEGORY_CONFIGURATION",
        trackAs: "categoryConfiguration",
        testId: "navi-category-configuration",
        url: "/backoffice/category/images",
        neededRights: ENTITLEMENTS.EX_CATEGORY_WRITE
    },
    searchCockpitSynonymsItem: {
        text: "NAVI_TITLES.SEARCH_COCKPIT_SYNONYMS",
        trackAs: "searchCockpitSynonyms",
        testId: "navi-search-cockpit-synonyms",
        url: "/backoffice/search/synonyms",
        neededRights: "sSA" // sdSearchAdminister
    },
    packagingTypesArticlesItem: {
        text: "NAVI_TITLES.ARTICLES_PACKAGING_TYPES",
        trackAs: "articlePackagingType",
        testId: "navi-articlePackagingType",
        url: "/evaluate.backofficeui/#/packaging_types",
        neededRights: ENTITLEMENTS.EV_OVERRIDES,
        isNonBackendUrl: true
    },
    transportTopItem: {
        text: "PAGE_TITLES.TRANSPORTATION",
        trackAs: "transportation",
        testId: "navi-top-transportation"
    },
    transportPlanningItem: {
        text: "NAVI_TITLES.TRANSPORTATION_PLANNING",
        trackAs: "transportationPlanning",
        testId: "navi-transportation-planning",
        url: "/transportation/planning",
        isOrderFulfillment: true
    },
    transportDeliveryItem: {
        text: "NAVI_TITLES.TRANSPORTATION_DELIVERY",
        trackAs: "transportationDelivery",
        testId: "navi-transportation-delivery",
        url: "/transportation/delivery",
        isOrderFulfillment: true
    },
    transportMasterDataItem: {
        text: "NAVI_TITLES.TRANSPORTATION_MASTERDATA",
        trackAs: "transportationMasterData",
        testId: "navi-transportation-master-data",
        url: "/transportation/planning",
        isOrderFulfillment: true
    },
    transportReportingItem: {
        text: "NAVI_TITLES.TRANSPORTATION_REPORTING",
        trackAs: "transportationReporting",
        testId: "navi-transportation-reporting",
        url: "/transportation/reporting",
        isOrderFulfillment: true
    },
    transportFrameTours: {
        text: "NAVI_TITLES.TRANSPORTATION_FRAME_TOURS",
        trackAs: "transportationFrameTours",
        testId: "navi-transportation-frame-tours",
        url: "/transportation/frame-tours",
        isOrderFulfillment: true
    },
    transportNonFsd: {
        text: "NAVI_TITLES.TRANSPORTATION_NON_FSD",
        trackAs: "transportationNonFsd",
        testId: "navi-transportation-/non-fsd",
        url: "/transportation/non-fsd",
        isOrderFulfillment: true
    }
};
Object.freeze(_items);

const _hierarchicMenu = [
    _items.employeeStartItem,
    {
        top: _items.customerTopItem,
        children: [_items.ordercaptureItem, _items.transportGoodsItem, _items.returnsItems],
        extraInfo: "customer"
    },
    _items.fastOrderEntryItem,
    {
        top: _items.orderFulfillmentTopItem,
        children: [_items.orderItem, _items.pickingItem, _items.combineordersItem, _items.wavesItem],
        isOrderFulfillment: true
    },
    {
        top: _items.depotTopItem,
        children: [
            _items.overviewsItem,
            _items.jobsItem,
            _items.articleLocationsItem,
            _items.locationItem,
            _items.storageGroupMappingsItem,
            _items.reservationItem,
            _items.labourManagementItem,
            _items.tillsconfsItem
        ],
        isOrderFulfillment: true
    },
    {
        top: _items.adminTopItem,
        beta: true,
        children: [
            _items.depotSettingsItem,
            _items.depotconfigItem,
            _items.storageGroupsItem,
            _items.importDeliveryItem,
            _items.depotAreasItem,
            _items.mopConfigItem,
            _items.depotPrinterConfigItem,
            _items.tourToGateAssignmentsItem
        ],
        isOrderFulfillment: true
    },
    {
        top: _items.transportTopItem,
        children: [
            _items.transportPlanningItem,
            _items.transportDeliveryItem,
            _items.transportMasterDataItem,
            _items.transportReportingItem,
            _items.transportFrameTours,
            _items.transportNonFsd
        ],
        isOrderFulfillment: true
    },
    {
        top: _items.backofficeTopItem,
        children: [
            _items.storelistManagementItem,
            _items.personallistManagement,
            _items.bannerManagementItem,
            _items.categoryConfigurationItem,
            _items.blackListArticlesItem,
            _items.articleGroupsItem,
            _items.leadtimeArticlesItem,
            _items.searchCockpitSynonymsItem,
            _items.packagingTypesArticlesItem
        ]
    },
    {
        top: _items.activationTopItem,
        children: [_items.storeActivationItem]
    },
    {
        top: _items.profileTopItem,
        children: [_items.logoutItem]
    },
    {
        top: _items.devTopItem,
        children: [_items.abExperimentItem]
    }
];

function employeeHasEntitlement(item: MenuItem) {
    if (item.neededRights) {
        return AuthUtil.hasEntitlementInAnyStore(item.neededRights);
    }
    return true;
}

const NaviDefs = {
    // returns all navigation items as heirachic list
    // TODO: add filtering for beta/tillsconfs and according to user roles
    getHierarchicMenuDef(): Array<MenuNode> {
        return _hierarchicMenu;
    },

    getCustomerFacingMenu(): {
        [x: string]: any;
    } {
        return _items.ordercaptureItem;
    },

    filterOutTopNodes(nodes: Array<MenuParent>): Array<MenuParent> {
        const nodeIds: string[] | undefined | null = CountryUtil.getOptionalConfig().filteredBackofficeNaviTabs;
        if (nodeIds) {
            return nodes.filter((node) => !nodeIds.includes(node.top?.testId as string));
        }
        return nodes;
    },
    // Filters the top items.
    // attention: the sub items are *not* filtered in *this* method,
    // they need to be filtered separatly with filteredSubItems()
    filteredTopItems(nodes: Array<MenuNode>, showBeta: boolean, showOrderFulfillment: boolean): Array<MenuNode> {
        const nodesFilteredByCountry = this.filterOutTopNodes(nodes as Array<MenuParent>);
        return nodesFilteredByCountry
            .filter((node) => NaviDevsUtil.createBetaFilter(showBeta)(node))
            .filter((node) => NaviDevsUtil.createOfFilter(showOrderFulfillment)(node))
            .filter((node) => node.url || NaviDefs.filteredSubItems(node, showBeta, showOrderFulfillment).length > 0);
    },

    filteredSubItems(parent: MenuNode, showBeta: boolean, showOrderFulfillment: boolean): Array<MenuItem> {
        if (!parent.children) {
            return [];
        } else {
            return parent.children
                .filter(NaviDevsUtil.createBetaFilter(showBeta))
                .filter(employeeHasEntitlement)
                .filter(NaviDevsUtil.createOfFilter(showOrderFulfillment));
        }
    }
};
export { MenuItem, MenuParent, MenuNode };
export default NaviDefs;

export const { getHierarchicMenuDef, getCustomerFacingMenu, filteredTopItems, filteredSubItems } = NaviDefs;
